import { useState, useRef, useEffect, useCallback, useLayoutEffect } from 'react';
import { Group } from '@/types';
import { OverflowGroupsPopover } from './OverflowGroupsPopover';
import { cn } from '@/lib/utils';

interface GroupBadgeListProps {
  groups: Group[];
  className?: string;
  maxRows?: number;
}

// Badge height (~30px) + gap (8px) * 2 rows + buffer
const MAX_HEIGHT_2_ROWS = 76;

export function GroupBadgeList({
  groups,
  className,
  maxRows = 2
}: GroupBadgeListProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const previousWidthRef = useRef<number>(0);
  const [visibleCount, setVisibleCount] = useState(groups.length);

  // After render, check row counts and adjust if needed
  const checkAndAdjustRows = useCallback(() => {
    if (!containerRef.current || groups.length === 0) {
      return;
    }

    const children = Array.from(containerRef.current.children) as HTMLElement[];
    if (children.length === 0) return;

    // Get row position for each child
    const childRows: { element: HTMLElement; top: number; isOverflow: boolean }[] = [];

    children.forEach((child) => {
      childRows.push({
        element: child,
        top: child.offsetTop,
        isOverflow: child.classList.contains('overflow-chip')
      });
    });

    // Get unique row positions sorted
    const uniqueTops = [...new Set(childRows.map(c => c.top))].sort((a, b) => a - b);

    // Count how many rows badges occupy (excluding overflow chip)
    const badgeRows = new Set(childRows.filter(c => !c.isOverflow).map(c => c.top));
    const overflowChip = childRows.find(c => c.isOverflow);

    // Check if overflow chip exists and is on its own row (row 3+)
    if (overflowChip) {
      const overflowRowIndex = uniqueTops.indexOf(overflowChip.top);
      const badgesOnOverflowRow = childRows.filter(c => !c.isOverflow && c.top === overflowChip.top).length;

      // If overflow is on row 3+ OR overflow is alone on any row, reduce visible count
      if (overflowRowIndex >= maxRows || badgesOnOverflowRow === 0) {
        if (visibleCount > 1) {
          setVisibleCount(prev => prev - 1);
          return;
        }
      }
    }

    // Check if badges exceed maxRows (without overflow chip consideration)
    if (badgeRows.size > maxRows) {
      // Find how many badges fit in first maxRows
      const sortedBadgeRows = [...badgeRows].sort((a, b) => a - b);
      const allowedTops = new Set(sortedBadgeRows.slice(0, maxRows));

      // Count badges in allowed rows
      let badgesInAllowedRows = 0;
      for (const child of childRows) {
        if (!child.isOverflow && allowedTops.has(child.top)) {
          badgesInAllowedRows++;
        }
      }

      if (badgesInAllowedRows < visibleCount) {
        setVisibleCount(badgesInAllowedRows);
        return;
      }
    }
  }, [groups.length, maxRows, visibleCount]);

  // Reset when groups change
  useEffect(() => {
    setVisibleCount(groups.length);
  }, [groups]);

  // Check layout after render - run synchronously for faster convergence
  useLayoutEffect(() => {
    // Double RAF to ensure layout is fully computed
    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        checkAndAdjustRows();
      });
    });
    return () => cancelAnimationFrame(raf);
  }, [checkAndAdjustRows, visibleCount]);

  // Handle resize - only reset when WIDTH changes (not height)
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Initialize previous width
    previousWidthRef.current = container.offsetWidth;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;

      const newWidth = entry.contentRect.width;
      // Only reset if width changed significantly (not just height from our algorithm)
      if (Math.abs(newWidth - previousWidthRef.current) > 1) {
        previousWidthRef.current = newWidth;
        setVisibleCount(groups.length);
      }
    });

    observer.observe(container);

    return () => observer.disconnect();
  }, [groups.length]);

  if (groups.length === 0) {
    return null;
  }

  const visibleProductions = groups.slice(0, visibleCount);
  const overflowCount = groups.length - visibleCount;
  const hasOverflow = overflowCount > 0;

  return (
    <div
      ref={containerRef}
      className={cn("flex flex-wrap gap-2 items-center overflow-hidden", className)}
      style={{ maxHeight: MAX_HEIGHT_2_ROWS }}
    >
      {visibleProductions.map((prod) => (
        <div
          key={prod.id}
          className="px-3 py-1.5 bg-[#8FB560]/10 border border-[#8FB560]/20 rounded-md"
        >
          <span className="text-sm font-medium whitespace-nowrap">{prod.name}</span>
        </div>
      ))}

      {hasOverflow && (
        <OverflowGroupsPopover
          groups={groups}
          overflowCount={overflowCount}
          className="overflow-chip"
        />
      )}
    </div>
  );
}
